import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { MemberLifecycleReportFilters, MemberLifecycleReportRow } from '../Models/MemberLifecycle/MemberLifecycle.model';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';

export const defaultMemberLifecycleReportFilters: MemberLifecycleReportFilters = {
    TeamId: 'NotSelected',
    ProviderId: 'NotSelected',
    MemberStatuses: '-1',
    IncomingStageKey: 'NotSelected',
    IncomingSubstageKey: '',
    IncludeInActiveProviders: false,
    IncludeInActiveTeams: false,
    Page: 0,
    PageSize: 20,
};

const memberLifecycleReport = createSlice({
    name: 'memberLifecycleReport',
    initialState: {
        rows: [] as MemberLifecycleReportRow[],
        filters: defaultMemberLifecycleReportFilters,
        totalCount: 0,
        isLoading: false,
        error: '',
    },
    reducers: {
        _setRows(state, action) {
            state.rows = action.payload;
        },
        _setFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
        },
        _setTotalCount(state, action) {
            state.totalCount = action.payload;
        },
        _setIsLoading(state, action) {
            state.isLoading = action.payload;
        },
        _setError(state, action) {
            state.error = action.payload;
        },
    },
});

const { _setRows, _setFilters, _setTotalCount, _setIsLoading, _setError } = memberLifecycleReport.actions;

const buildQueryString = (filters: MemberLifecycleReportFilters) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }

        if (typeof value === 'boolean') {
            params.append(key, value ? 'true' : 'false');
            return;
        }

        if (value !== '') {
            params.append(key, value.toString());
        }
    });
    return params.toString();
};

export const setMemberLifecycleReportFilters =
    (filters: Partial<MemberLifecycleReportFilters>): AppThunk =>
    async (dispatch) => {
        dispatch(_setFilters(filters));
    };

export const fetchMemberLifecycleReport =
    (filters: MemberLifecycleReportFilters = defaultMemberLifecycleReportFilters): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsLoading(true));
        dispatch(_setError(''));
        try {
            const queryString = buildQueryString(filters);
            const response = await axios.get(`${API_URL}/MemberLifecycle/report${queryString ? `?${queryString}` : ''}`);
            dispatch(_setRows(response.data?.Data ?? []));
            dispatch(_setTotalCount(response.data?.TotalCount ?? 0));
        } catch {
            dispatch(_setError('Unable to load member lifecycle report.'));
            dispatch(_setRows([]));
            dispatch(_setTotalCount(0));
        } finally {
            dispatch(_setIsLoading(false));
        }
    };

export default memberLifecycleReport.reducer;
